import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, LogOut, User, Settings, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

function BasketButton({ className = "", id = "nav-basket" }) {
  const { count, setOpen } = useCart();
  return (
    <Button variant="ghost" size="icon" onClick={() => setOpen(true)} className={`relative ${className}`} aria-label="Basket" data-testid={`${id}-btn`}>
      <ShoppingBag className="w-5 h-5 text-slate-700" />
      {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`${id}-count`}>{count}</span>}
    </Button>
  );
}

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Programs",
    children: [
      { label: "Future Teachers", to: "/future-teachers" },
      { label: "International Teachers", to: "/international-teachers" },
      { label: "Career Support", to: "/career-support" },
      { label: "Mentors & Schools", to: "/mentors-schools" },
      { label: "Universities", to: "/universities" },
      { label: "Workshop Slide Decks", to: "/workshop-series" },
      { label: "AI Workshops", to: "/ai-workshops" },
    ],
  },
  { label: "Tutoring Marketplace", to: "/tutoring" },
  { label: "Resources", to: "/resources" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const isActive = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80" data-testid="main-navbar">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[88px]">
          <Link to="/" className="flex items-center shrink-0 group" data-testid="nav-logo">
            <img src="/logo-full.png" alt="Teaching Pathways — Learn · Mentor · Grow"
              width="1200" height="430"
              className="h-12 sm:h-14 w-auto max-w-[240px] sm:max-w-none object-contain transition-transform group-hover:scale-[1.02]" />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((item) =>
              item.children ? (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-teal-700 hover:bg-slate-50 transition-colors" data-testid="nav-programs-trigger">
                      {item.label} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-60">
                    {item.children.map((c) => (
                      <DropdownMenuItem key={c.to} asChild>
                        <Link to={c.to} data-testid={`nav-drop-${c.to.slice(1)}`}>{c.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.to) ? "text-teal-700" : "text-slate-700 hover:text-teal-700 hover:bg-slate-50"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <BasketButton />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-slate-200 hover:border-teal-400 transition-colors" data-testid="nav-user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm"><User className="w-4 h-4" /></span>
                    )}
                    <span className="text-sm font-medium text-slate-700 max-w-[100px] truncate">{user.name || "Account"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild data-testid="nav-account"><Link to="/account"><User className="w-4 h-4 mr-2" /> My Account</Link></DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem asChild data-testid="nav-admin"><Link to="/admin"><Settings className="w-4 h-4 mr-2" /> Admin</Link></DropdownMenuItem>}
                  <DropdownMenuItem onClick={logout} data-testid="nav-logout"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" onClick={login} className="text-slate-700" data-testid="nav-login-btn">Sign in</Button>
            )}
            <Button asChild className="bg-teal-600 hover:bg-teal-700 rounded-full px-5" data-testid="nav-cta-consult">
              <Link to="/contact">Book Consultation</Link>
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-1">
            <BasketButton id="mobile-basket" />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="nav-mobile-trigger"><Menu className="w-6 h-6" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto">
                <SheetTitle className="font-display text-lg mb-4">Menu</SheetTitle>
                <div className="flex flex-col gap-1">
                  {NAV.flatMap((item) => item.children ? item.children : [item]).map((item) => (
                    <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                      className="px-3 py-2.5 rounded-md text-slate-700 hover:bg-slate-50 font-medium"
                      data-testid={`mobile-nav-${item.to.slice(1) || "home"}`}>
                      {item.label}
                    </Link>
                  ))}
                  <div className="border-t border-slate-200 mt-3 pt-3 flex flex-col gap-2">
                    {user ? (
                      <>
                        <Button asChild variant="outline" data-testid="mobile-account"><Link to="/account" onClick={() => setOpen(false)}>My Account</Link></Button>
                        {user.is_admin && <Button asChild variant="outline" data-testid="mobile-admin"><Link to="/admin" onClick={() => setOpen(false)}>Admin</Link></Button>}
                        <Button variant="outline" onClick={() => { logout(); setOpen(false); }} data-testid="mobile-logout">Sign out</Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={login} data-testid="mobile-login">Sign in with Google</Button>
                    )}
                    <Button asChild className="bg-teal-600 hover:bg-teal-700"><Link to="/contact" onClick={() => setOpen(false)}>Book Consultation</Link></Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
